import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, ShieldCheck, Users, Clock, MapPin, Phone, Mail, MessageCircle, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

import useSWR from 'swr';
import { publicSettings } from '../lib/api';

export const About = () => {
  const { data: settings } = useSWR('publicSettings', publicSettings);
  const siteName = settings?.siteName || 'Watch Trader';

  return (
    <div className="bg-white">
      <div className="bg-neutral-100 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-2 text-xs text-neutral-500 mb-3">
            <Link to="/" className="hover:text-[#b8892d]">Home</Link>
            <ChevronRight size={12}/>
            <span className="text-neutral-800">About Us</span>
          </div>
          <h1 className="font-serif text-4xl text-[#1a1a1a]">About {siteName}</h1>
          <p className="text-neutral-600 mt-2 text-sm md:text-base font-medium">
            10+ Years of Trust. Premium Watches at the Right Price.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-12 items-center">
        <img 
          src="https://images.unsplash.com/photo-1618215650148-e8e61eae521c?crop=entropy&cs=srgb&fm=jpg&w=900&q=85" 
          alt="Luxury Watch Collection" 
          className="w-full h-[520px] object-cover rounded-md shadow-sm"
        />
        <div className="space-y-6">
          <div className="text-[#b8892d] text-xs tracking-[0.3em] uppercase font-semibold">Our Story</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] leading-tight">
            ABOUT US
          </h2>
          
          <p className="text-neutral-800 font-medium text-base leading-relaxed">
            Our company has been in India with the business with well over 10+ years. We provide all the Premium and Luxury watches in India with warranty.
          </p>
          
          <p className="text-neutral-700 leading-relaxed">
            Our main aim is to fulfill the dream of common man by providing the premium quality watches at an affordable price. We provide 100% Swiss ETA watches from reputed factories like ZF, EW, MP, BV, TW, GM etc.
          </p>
          
          <p className="text-neutral-700 leading-relaxed">
            Welcome to {siteName}, your trusted destination for premium and luxury watches in India. With over 10 years of experience, we are committed to offering stylish, quality-checked watches with reliable movements and excellent value.
          </p>
          
          <p className="text-neutral-700 leading-relaxed">
            Our collection includes premium watches, luxury-style watches, and selected Swiss ETA movement watches, carefully sourced from reputed manufacturers. Every watch is selected with a focus on quality, craftsmanship, performance, and customer satisfaction.
          </p>
          
          <p className="text-neutral-700 leading-relaxed">
            We believe everyone deserves to experience the look and feel of a premium timepiece without an unreasonable price tag. With Pan-India delivery, Cash on Delivery, and warranty support, we make buying premium watches online simple and reliable.
          </p>

          <div className="pt-4 border-t border-neutral-100">
            <p className="font-serif text-lg text-[#b8892d] italic">
              {siteName} — Premium Watches. Trusted Quality. Timeless Style.
            </p>
          </div>
        </div>
      </div>

      <section className="bg-neutral-50 py-16 border-t border-neutral-150">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-[#b8892d] text-xs tracking-[0.3em] uppercase mb-3 font-semibold font-medium">Why Choose Us</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a1a]">Why Choose {siteName}?</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: '10+ Years of Experience', desc: 'A decade of trust and commitment in offering premium quality watches with excellent value.' },
              { title: 'Premium & Luxury Watches', desc: 'Curated designs and premium luxury options delivering high appeal and elegant aesthetics.' },
              { title: 'Swiss ETA Movement Options', desc: 'Selected options featuring high-performance Swiss ETA movements from renowned factories.' },
              { title: 'Quality-Checked Products', desc: 'Each watch is strictly checked to ensure performance, craftsmanship and robust build quality.' },
              { title: '1–2 Year Machine Warranty', desc: 'Peace of mind with reliable machine warranty support on all our selections.' },
              { title: 'Pan-India Delivery', desc: 'Quick and secure logistics network to send watches directly to your home.' },
              { title: 'Cash on Delivery Available', desc: 'Easy and secure cash on delivery support across all pins in India.' },
              { title: 'Affordable Premium Luxury', desc: 'Experience exquisite premium luxury looks without unreasonable price tags.' },
            ].map((item, index) => (
              <div 
                key={index} 
                className="bg-white border border-neutral-200 p-6 rounded-md hover:border-[#b8892d] transition-all duration-300 hover:shadow-sm"
              >
                <div className="w-10 h-10 rounded-full bg-[#b8892d]/10 text-[#b8892d] flex items-center justify-center mb-4">
                  <ShieldCheck size={20} />
                </div>
                <h3 className="font-serif text-lg text-[#1a1a1a] mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const Contact = () => {
  const { toast } = useToast();
  const { data: settings } = useSWR('publicSettings', publicSettings);
  const siteName = settings?.siteName || 'Watch Trader';
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ title: 'Message Sent', description: 'We will get back to you within 24 hours.' });
    setForm({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="bg-white">
      <div className="bg-neutral-100 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-2 text-xs text-neutral-500 mb-3">
            <Link to="/" className="hover:text-[#b8892d]">Home</Link>
            <ChevronRight size={12}/>
            <span className="text-neutral-800">Contact</span>
          </div>
          <h1 className="font-serif text-4xl text-[#1a1a1a]">Get in Touch</h1>
          <p className="text-neutral-600 mt-2 max-w-xl">Have a question about a watch, order, or need styling advice? Our team is ready to help.</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-12">
        <div>
          <h2 className="font-serif text-2xl mb-6">Contact Information</h2>
          <div className="space-y-5">
            {[
              { i: MapPin, t: 'Store Address', d: settings?.contactAddress || 'New Delhi, India — Serving customers across India' },
              { i: Phone, t: 'Phone / WhatsApp', d: settings?.contactPhone || '+91 98765 43210' },
              { i: Mail, t: 'Email', d: settings?.contactEmail || 'support@watchtrader.in' },
              { i: MessageCircle, t: 'Live Chat', d: 'Mon - Sat: 10:00 AM - 8:00 PM' },
            ].map((c) => (
              <div key={c.t} className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-[#b8892d]/10 text-[#b8892d] flex items-center justify-center flex-shrink-0">
                  <c.i size={18}/>
                </div>
                <div>
                  <div className="font-medium text-neutral-800">{c.t}</div>
                  <div className="text-neutral-600 text-sm">{c.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={handleSubmit} className="bg-neutral-50 p-8 space-y-4">
          <h2 className="font-serif text-2xl mb-2">Send a Message</h2>
          <div><Label>Name</Label><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 bg-white"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 bg-white"/></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 bg-white"/></div>
          </div>
          <div><Label>Message</Label><Textarea required rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="mt-1 bg-white"/></div>
          <Button type="submit" className="w-full bg-[#b8892d] hover:bg-[#a07826] h-11 uppercase tracking-widest text-xs">Send Message</Button>
        </form>
      </div>
    </div>
  );
};
